import useModel from '@/hooks/useModel';
import useModels from '@/hooks/useModels';
import useSpecs, { CarTrimSpec } from '@/hooks/useSpecs';
import useEmblaCarousel from 'embla-carousel-react';
import { AnimatePresence, motion } from 'framer-motion';
import { lazy, useCallback, useEffect, useState } from 'react';
import { ModelType } from 'types';
import { locationIsKia } from 'utils/helpers';
import { trackEvent } from 'utils/tracking';
import CardContainer from './_components/card-container';
import {
  DataCardStoreProvider,
  useDataCardStoreContext,
} from './store/data-card-store-provider';

const Snippet = lazy(() => import('../../components/Snippet'));

type DataCardProps = {
  modelCode?: string;
};

function DataCardInternal({ modelCode }: DataCardProps) {
  const showSelect = !locationIsKia() && !modelCode;

  const { models } = useModels();
  const { model: fetchedModel } = useModel(modelCode);

  const selection = useDataCardStoreContext((state) => state.selectedModel);
  const setSelectedModel = useDataCardStoreContext(
    (state) => state.setSelectedModel,
  );

  const selectedModel = modelCode ? fetchedModel : (selection ?? models[0]);

  const { specs, isLoading } = useSpecs(selectedModel?.code);

  return (
    <div>
      {showSelect ? (
        <>
          <div className="max-w-lg mb-6 mx-auto my-4">
            <label className="block text-xs font-bold uppercase tracking-wide text-dark-blue/70 mb-1">
              Välj modell
            </label>
            <select
              value={selectedModel?.code ?? ''}
              onChange={(e) => {
                const model = models.find((m) => m.code === e.target.value);
                if (model) {
                  setSelectedModel(model);
                }
              }}
              className="w-full h-11 px-3 rounded-md border border-dark-blue/10 bg-white text-sm font-medium text-dark-blue focus:outline-none focus:ring-2 focus:ring-dark-blue/20"
            >
              <option value="" disabled>
                Välj modell
              </option>
              {models.map((model) => (
                <option key={model.id} value={model.code}>
                  {model.name}
                </option>
              ))}
            </select>
          </div>
          <Snippet
            page="data-card"
            props={`modelCode="${selectedModel?.code ?? ''}"`}
          />
        </>
      ) : null}
      {selectedModel ? (
        <CardContainer model={selectedModel}>
          <UnifiedComparer
            specs={specs}
            model={selectedModel}
            isLoading={isLoading}
          />
        </CardContainer>
      ) : null}
    </div>
  );
}

function DataCard({ modelCode }: DataCardProps) {
  return (
    <DataCardStoreProvider>
      <DataCardInternal modelCode={modelCode} />
    </DataCardStoreProvider>
  );
}

export default DataCard;

const UnifiedComparer = ({
  specs,
  model,
  isLoading,
}: {
  specs: CarTrimSpec[];
  model: ModelType;
  isLoading?: boolean;
}) => {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const isSectionOpen = (key: string, defaultOpen: boolean) =>
    openSections[key] ?? defaultOpen;
  const toggleSection = (key: string, defaultOpen: boolean) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !isSectionOpen(key, defaultOpen),
    }));
  };
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'center',
    dragFree: false,
    containScroll: 'trimSnaps',
    slidesToScroll: 'auto',
  });

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi],
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi],
  );

  const onSelect = useCallback((api: any) => {
    const canScrollPrev = api.canScrollPrev();
    const canScrollNext = api.canScrollNext();
    setPrevBtnEnabled(canScrollPrev);
    setNextBtnEnabled(canScrollNext);
    setIsOverflowing(canScrollPrev || canScrollNext);
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit({ watchDrag: isOverflowing });
  }, [emblaApi, isOverflowing]);

  if (isLoading) return <SpecsSkeleton />;

  if (!specs.length) return null;

  const showGridAtLg = specs.length <= 3;

  return (
    <div
      data-layout={showGridAtLg ? 'grid' : 'carousel'}
      data-scrollable={isOverflowing}
      data-prev={prevBtnEnabled}
      data-next={nextBtnEnabled}
      className="group grid w-full mt-10 relative max-w-screen-2xl mx-auto data-[layout=carousel]:select-none"
    >
      {prevBtnEnabled && (
        <button
          onClick={scrollPrev}
          className="absolute left-0 lg:left-2 top-1/2 z-10 bg-white border border-dark-blue/10 grid place-content-center size-11 rounded-full hover:bg-slate-50 transition-all "
          aria-label="Föregående"
        >
          <div className="size-4 text-dark-blue">
            <ChevronLeftIcon />
          </div>
        </button>
      )}

      {nextBtnEnabled && (
        <button
          onClick={scrollNext}
          className="absolute right-0 lg:right-2 top-1/2 z-10 bg-white border border-dark-blue/10 grid place-content-center size-11 rounded-full hover:bg-slate-50 transition-all"
          aria-label="Nästa"
        >
          <div className="size-4 text-dark-blue">
            <ChevronRightIcon />
          </div>
        </button>
      )}
      <div
        className="overflow-hidden transition-all group-data-[scrollable=true]:cursor-grab group-data-[scrollable=true]:active:cursor-grabbing group-data-[prev=true]:lg:[mask-image:linear-gradient(to_right,transparent,black_5%)] group-data-[next=true]:lg:[mask-image:linear-gradient(to_left,transparent,black_5%)]"
        ref={emblaRef}
      >
        <div className="flex touch-pan-y pb-12 group-data-[layout=grid]:lg:grid group-data-[layout=grid]:lg:grid-cols-3 group-data-[layout=grid]:lg:gap-4">
          {specs.map((trim) => {
            return (
              <div
                key={trim.id}
                className="min-w-0 bg-white rounded-lg border border-dark-blue/10 flex flex-col flex-[0_0_85vw] md:flex-[0_0_calc(50%-1rem)] ml-4 group-data-[layout=grid]:lg:flex-none group-data-[layout=grid]:lg:ml-0 group-data-[layout=carousel]:lg:flex-[0_0_calc(30%-1rem)]"
              >
                <div className="p-4 border-b border-dark-blue/10 min-h-[93px]">
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="text-lg font-extrabold uppercase text-dark-blue tracking-wide flex">
                      {trim.name}
                    </h3>
                  </div>
                </div>

                <div className="px-4 py-4 pt-0 flex flex-col flex-grow">
                  <AccordionSection
                    title="Höjdpunkter"
                    className="border-b-0 border-t-0"
                    isOpen={isSectionOpen('features', true)}
                    onToggle={() => toggleSection('features', true)}
                  >
                    <ul className="flex flex-col gap-2">
                      {trim.highlights.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="shrink-0 mt-0.5 text-slate-900">
                            <CheckIcon />
                          </div>
                          <span className="text-sm font-medium text-slate-700 leading-snug">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </AccordionSection>

                  <div className="mt-auto flex flex-col">
                    {trim.specifications.map((group, groupIndex) => (
                      <AccordionSection
                        key={group.category}
                        title={group.label}
                        className={groupIndex === 0 ? 'border-t' : undefined}
                        isOpen={isSectionOpen(group.category, groupIndex === 0)}
                        onToggle={() =>
                          toggleSection(group.category, groupIndex === 0)
                        }
                      >
                        <div className="grid grid-cols-2 gap-2">
                          {group.items.map((item) => (
                            <MiniStat
                              key={item.key}
                              label={item.title}
                              value={
                                item.unit
                                  ? `${item.value} ${item.unit}`
                                  : item.value
                              }
                            />
                          ))}
                        </div>
                      </AccordionSection>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {model.fact_sheet && model.fact_sheet.length > 0 ? (
        <a
          href={model.fact_sheet}
          target="_blank"
          rel="noreferrer noopener"
          onClick={() =>
            trackEvent({
              event: 'request_brochure',
              form_step: 'download',
              vehicle_nameplate: model.name,
              vehicle_type: model.type?.name,
              vehicle_year: undefined,
              vehicle_trim: undefined,
              document_type_list: 'fact sheet',
            })
          }
          className="flex items-center min-w-72 px-4 w-fit mx-auto justify-center gap-2 h-12 border bg-black text-white no-underline hover:underline"
        >
          <span>Ladda ner fullständigt faktablad</span>
        </a>
      ) : null}
    </div>
  );
};

function SpecsSkeleton() {
  return (
    <div className="grid w-full mt-10 grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 max-w-screen-2xl mx-auto">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="min-w-0 bg-white rounded-lg border border-dark-blue/10 flex flex-col animate-pulse"
        >
          <div className="p-4 border-b border-dark-blue/10">
            <div className="h-6 w-2/3 bg-slate-200 rounded" />
            <div className="mt-2 h-[34px] w-full" />
          </div>
          <div className="px-4 py-4 pt-0 flex flex-col flex-grow gap-4">
            <div className="flex flex-col gap-3 py-3">
              {Array.from({ length: 10 }).map((_, j) => (
                <div key={j} className="h-4 w-11/12 bg-slate-100 rounded" />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {Array.from({ length: 4 }).map((_, j) => (
                <div
                  key={j}
                  className="h-14 rounded-md bg-kia-grey border border-dark-blue/10"
                />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col p-3 rounded-md bg-kia-grey border border-dark-blue/10">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs font-bold uppercase tracking-wide text-dark-blue/70 truncate">
          {label}
        </span>
      </div>
      <span className="text-sm font-bold text-slate-900 tracking-tight">
        {value}
      </span>
    </div>
  );
}

const AccordionSection = ({
  title,
  children,
  isOpen,
  onToggle,
  className,
}: {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  className?: string;
}) => {
  return (
    <div className={`border-b border-dark-blue/10 ${className ?? ''}`}>
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full py-3 text-left group"
      >
        <span className="text-xs font-bold uppercase tracking-widest text-dark-blue/70 group-hover:text-dark-blue transition-colors">
          {title}
        </span>
        <div
          className={`text-dark-blue/50 transition-transform duration-300 ${isOpen ? '-rotate-180' : ''}`}
        >
          <ChevronDownIcon />
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
            className="overflow-hidden"
          >
            <div className="pb-4 pt-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ChevronDownIcon = () => (
  <svg
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="2.5"
    stroke="currentColor"
    className="size-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
    />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 19.5L8.25 12l7.5-7.5"
    />
  </svg>
);

const ChevronRightIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8.25 4.5l7.5 7.5-7.5 7.5"
    />
  </svg>
);
const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-4"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);
